import React, { useMemo, useState } from 'react'
import { ArrowLeft, RotateCw, Clock } from 'lucide-react'
import { EVENTS } from './data.js'
import { tr, useLang } from '../lib/lang.js'
import { LangToggle } from '../lib/LangToggle.jsx'
import { rngFrom, shuffle } from '../lib/random.js'
import { awardBp } from '../lib/store.js'

const ROUND = 6
const PER = 4

export function CronicasGame({ onBack, onComplete }) {
  const [lang, setLang] = useLang()
  const [roundKey, setRoundKey] = useState(0)
  const [roundIndex, setRoundIndex] = useState(0)
  const [order, setOrder] = useState([])
  const [score, setScore] = useState(0)
  const [result, setResult] = useState(null) // { correct, byYear }
  const [done, setDone] = useState(false)

  const rounds = useMemo(() => {
    const rng = rngFrom((roundKey + 1) * 86753)
    const pool = shuffle(rng, EVENTS)
    const out = []
    for (let i = 0; i < ROUND; i++) {
      const group = pool.slice(i * PER, i * PER + PER)
      if (group.length < PER) break
      out.push(shuffle(rng, group))
    }
    return out
  }, [roundKey])

  const current = rounds[roundIndex]

  const check = (finalOrder) => {
    const byYear = [...finalOrder].sort((a, b) => a.year - b.year)
    const correct = byYear.every((e, i) => e.year === finalOrder[i].year)
    setResult({ correct, byYear })
    if (correct) setScore((s) => s + 1)
  }

  const tap = (ev) => {
    if (result) return
    if (order.some((e) => e.year === ev.year)) return
    const next = [...order, ev]
    setOrder(next)
    if (next.length === PER) check(next)
  }

  const nextRound = () => {
    if (roundIndex + 1 >= rounds.length) {
      awardBp(score * 15)
      onComplete?.(score * 15)
      setDone(true)
      return
    }
    setRoundIndex((i) => i + 1)
    setOrder([])
    setResult(null)
  }

  const restart = () => {
    setRoundKey((k) => k + 1)
    setRoundIndex(0)
    setOrder([])
    setScore(0)
    setResult(null)
    setDone(false)
  }

  if (done) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[55vh] text-center space-y-5">
        <Clock size={40} className="text-amber-400" />
        <h2 className="text-xl font-black text-white">
          {score}/{rounds.length}
        </h2>
        <p className="text-xs text-slate-400">
          {tr(lang, 'Rondas ordenadas correctamente', 'Rounds ordered correctly')}
        </p>
        <div className="flex gap-2 w-full max-w-xs">
          <button
            type="button"
            onClick={restart}
            className="flex-1 py-3 rounded-xl bg-amber-500 text-slate-950 font-black text-sm cursor-pointer flex items-center justify-center gap-2"
          >
            <RotateCw size={16} /> {tr(lang, 'Otra vez', 'Again')}
          </button>
          <button
            type="button"
            onClick={onBack}
            className="flex-1 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white font-black text-sm cursor-pointer"
          >
            {tr(lang, 'Salir', 'Exit')}
          </button>
        </div>
      </div>
    )
  }

  const byYear = result ? [...current].sort((a, b) => a.year - b.year) : []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-bold text-slate-400 hover:text-white cursor-pointer"
        >
          <ArrowLeft size={16} /> Volver
        </button>
        <LangToggle lang={lang} onChange={setLang} />
      </div>

      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span className="font-bold uppercase tracking-wider">Crónicas</span>
        <span>
          {roundIndex + 1}/{rounds.length} · {score} ✓
        </span>
      </div>

      <p className="text-sm font-bold text-white">
        {tr(lang, 'Toca los hechos del más antiguo al más reciente.', 'Tap the events from oldest to newest.')}
      </p>

      <div className="space-y-2.5">
        {current.map((ev) => {
          const pos = order.findIndex((e) => e.year === ev.year)
          const chosen = pos >= 0
          const isRight = result && byYear.findIndex((e) => e.year === ev.year) === pos
          return (
            <button
              key={ev.year}
              type="button"
              disabled={chosen || !!result}
              onClick={() => tap(ev)}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border text-left transition-all cursor-pointer disabled:cursor-default ${
                result
                  ? isRight
                    ? 'bg-emerald-950/50 border-emerald-500/40'
                    : 'bg-rose-950/50 border-rose-500/40'
                  : chosen
                    ? 'bg-amber-950/40 border-amber-500/40'
                    : 'bg-[#0e131f] border-slate-800 hover:border-amber-500/50'
              }`}
            >
              <span
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${
                  chosen ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-400'
                }`}
              >
                {chosen ? pos + 1 : ''}
              </span>
              <span className="text-xl">{ev.icon}</span>
              <span className="text-sm font-bold text-white flex-1">
                {tr(lang, ev.label.es, ev.label.en)}
              </span>
              {result && <span className="text-xs font-black text-amber-300">{ev.year}</span>}
            </button>
          )
        })}
      </div>

      {result && (
        <div className="space-y-3">
          <div
            className={`text-center text-sm font-black py-2 rounded-xl border ${
              result.correct
                ? 'bg-emerald-950/60 text-emerald-300 border-emerald-500/40'
                : 'bg-rose-950/60 text-rose-300 border-rose-500/40'
            }`}
          >
            {result.correct ? tr(lang, '¡Orden correcto!', 'Correct order!') : tr(lang, 'Orden incorrecto', 'Wrong order')}
          </div>
          <button
            type="button"
            onClick={nextRound}
            className="w-full py-3 rounded-xl bg-amber-500 text-slate-950 font-black text-sm cursor-pointer"
          >
            {tr(lang, 'Siguiente', 'Next')}
          </button>
        </div>
      )}
    </div>
  )
}
